/**
 * Fetch wrapper for server-side (SSR) API calls to the monolith.
 *
 * SSR data fetches run inside the request path, where a single failed or hung
 * fetch becomes a user-facing error. These calls target the in-cluster
 * monolith service over plain HTTP, so:
 *
 * - `X-Forwarded-Proto: https` restores the original scheme that the
 *   TLS-terminating edge would normally assert (Django trusts it via
 *   SECURE_PROXY_SSL_HEADER).
 * - An overall deadline keeps a hung upstream from holding renders open.
 *   Only real production gets the tight, in-cluster-network deadline —
 *   `STAGE_NAME` (see `next.config.js`) is `"production"` only for that
 *   deployment; local dev and every deployed non-prod stage (staging/wtf/
 *   integration) routinely take longer than that to respond, so they get a
 *   much longer one instead of erroring on every page load.
 * - Connection-phase failures are retried once for idempotent methods; the
 *   request was never sent, so a retry cannot duplicate work.
 */

const PRODUCTION_TIMEOUT_MS = 10000;
const NON_PRODUCTION_TIMEOUT_MS = 30000;
const TIMEOUT_MS =
  process.env.STAGE_NAME === "production"
    ? PRODUCTION_TIMEOUT_MS
    : NON_PRODUCTION_TIMEOUT_MS;
const RETRY_DELAY_MS = 100;

// undici surfaces connection-phase failures as `TypeError: fetch failed`
// with the underlying error (and its `code`) in `cause`. EAI_AGAIN is
// transient DNS; ENOTFOUND and friends are definitive and excluded.
const RETRYABLE_CAUSE_CODES = new Set([
  "UND_ERR_CONNECT_TIMEOUT",
  "ECONNREFUSED",
  "ECONNRESET",
  "EAI_AGAIN",
]);

const RETRYABLE_METHODS = new Set(["GET", "HEAD"]);

export const isRetryableConnectionError = (error: unknown): boolean => {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const cause = (error as { cause?: unknown }).cause as
    | { code?: unknown }
    | undefined;
  const code = cause?.code;
  return typeof code === "string" && RETRYABLE_CAUSE_CODES.has(code);
};

const composeSignal = (callerSignal?: AbortSignal | null): AbortSignal => {
  // AbortSignal.timeout exists on our Node runtime but is missing from this
  // TypeScript version's lib definitions.
  const timeoutSignal = (
    AbortSignal as unknown as { timeout: (ms: number) => AbortSignal }
  ).timeout(TIMEOUT_MS);
  if (!callerSignal) {
    return timeoutSignal;
  }
  // AbortSignal.any exists on our Node runtime but is missing from this
  // TypeScript version's lib definitions.
  const abortSignalAny = (
    AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }
  ).any;
  return abortSignalAny
    ? abortSignalAny([callerSignal, timeoutSignal])
    : callerSignal;
};

export const serverApiFetch = (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const method = String(options.method || "GET").toUpperCase();
  const headers = new Headers(options.headers);
  headers.set("X-Forwarded-Proto", "https");

  const attempt = () =>
    fetch(url, { ...options, headers, signal: composeSignal(options.signal) });

  return attempt().catch(async (error) => {
    if (!RETRYABLE_METHODS.has(method) || !isRetryableConnectionError(error)) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    return attempt();
  });
};
